// Zona para arrastrar o elegir un archivo (`.ag-drop`, handoff
// autogestión): logo de la empresa, planilla de tiendas o de
// invitaciones, documento de contexto de la IA. Antes de esto no había
// ningún drag & drop en el dashboard.
//
// Accesibilidad: arrastrar es solo de mouse, así que el camino de
// teclado y lector de pantalla es un <input type="file"> real, .sr-only
// dentro de un <label> con forma de botón — el foco cae en el input
// (anillo dibujado en el label por :focus-within) y su nombre es el
// texto del label. La validación de tipo y tamaño corre en los dos
// caminos: `accept` en el input solo filtra el diálogo del sistema, no
// lo que se suelta encima de la zona.
//
// El componente no sube nada: entrega los File aceptados a `onFiles` y
// el consumidor decide (presign, preview de planilla, etc.).

import { useId, useRef, useState } from "react";
import type { ChangeEvent, DragEvent, ReactNode } from "react";
import { Icon } from "./icons";

export type DropzoneProps = {
  onFiles: (files: File[]) => void;
  /** Frase principal, p. ej. «Arrastra la planilla aquí». */
  title: string;
  /** Formatos y límites en palabras, p. ej. «XLSX o CSV · una fila por persona». */
  hint?: ReactNode;
  /** Mismo formato que el atributo HTML: «.xlsx,.csv», «image/png,image/svg+xml». */
  accept?: string;
  /** Tamaño máximo por archivo, en bytes. */
  maxSize?: number;
  multiple?: boolean;
  disabled?: boolean;
  /** Texto del botón; por defecto «Elegir archivo». */
  buttonLabel?: string;
  /** Frase del title del botón; por defecto describe lo que abre. */
  buttonTitle?: string;
  /** Ícono de la zona: «upload» (planillas, documentos) o «image» (logos). */
  icon?: "upload" | "image";
};

function UploadIcon() {
  return (
    <svg
      width={24}
      height={24}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12 16V4M7 9l5-5 5 5" />
      <path d="M5 20h14" />
    </svg>
  );
}

const MIME_LABELS: Record<string, string> = {
  "image/png": "PNG",
  "image/svg+xml": "SVG",
  "image/jpeg": "JPG",
  "image/webp": "WEBP",
  "image/*": "imágenes",
  "application/pdf": "PDF",
  "text/csv": "CSV",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
  "application/vnd.ms-excel": "XLS",
};

function acceptTokens(accept: string): string[] {
  return accept
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
}

/** «.xlsx,.csv» → «XLSX o CSV»; «image/png,image/svg+xml» → «PNG o SVG». */
export function describeAccept(accept: string): string {
  const labels: string[] = [];
  for (const token of acceptTokens(accept)) {
    const label = token.startsWith(".")
      ? token.slice(1).toUpperCase()
      : (MIME_LABELS[token] ?? (token.split("/")[1] ?? token).toUpperCase());
    if (!labels.includes(label)) labels.push(label);
  }
  if (labels.length <= 1) return labels[0] ?? "";
  return `${labels.slice(0, -1).join(", ")} o ${labels[labels.length - 1]}`;
}

export function fileMatchesAccept(file: File, accept: string): boolean {
  const tokens = acceptTokens(accept);
  if (tokens.length === 0) return true;
  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();
  return tokens.some((token) => {
    if (token.startsWith(".")) return name.endsWith(token);
    if (token.endsWith("/*")) return type.startsWith(token.slice(0, -1));
    return type === token;
  });
}

const sizeFormat = new Intl.NumberFormat("es-CL", { maximumFractionDigits: 1 });

export function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${sizeFormat.format(bytes / (1024 * 1024))} MB`;
  return `${sizeFormat.format(Math.max(1, Math.round(bytes / 1024)))} KB`;
}

/** Primer motivo de rechazo, en español, o null si todos pasan. */
function rejection(
  files: File[],
  {
    accept,
    maxSize,
    multiple,
  }: { accept: string | undefined; maxSize: number | undefined; multiple: boolean }
): string | null {
  if (!multiple && files.length > 1) return "Elige un solo archivo.";
  for (const file of files) {
    if (accept && !fileMatchesAccept(file, accept)) {
      return `«${file.name}» no es un tipo de archivo permitido. Usa ${describeAccept(accept)}.`;
    }
    if (maxSize !== undefined && file.size > maxSize) {
      return `«${file.name}» pesa ${formatBytes(file.size)}; el máximo es ${formatBytes(maxSize)}.`;
    }
  }
  return null;
}

export function Dropzone({
  onFiles,
  title,
  hint,
  accept,
  maxSize,
  multiple = false,
  disabled = false,
  buttonLabel = "Elegir archivo",
  buttonTitle,
  icon = "upload",
}: DropzoneProps) {
  const hintId = useId();
  const errorId = useId();
  const [over, setOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // dragenter/dragleave también saltan al cruzar los hijos de la zona;
  // el contador evita que el resaltado parpadee.
  const depth = useRef(0);

  function take(list: FileList | null) {
    const files = list ? Array.from(list) : [];
    if (files.length === 0) return;
    const reason = rejection(files, { accept, maxSize, multiple });
    setError(reason);
    if (reason === null) onFiles(files);
  }

  // preventDefault siempre, también deshabilitada: sin él en dragover el
  // navegador no dispara drop y abre el archivo en la pestaña, sacando
  // al usuario del dashboard (y perdiendo el drawer abierto). Deshabilitada
  // solo se niega el efecto (cursor «no permitido») y no se resalta.
  function onDragEnter(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    if (disabled) {
      e.dataTransfer.dropEffect = "none";
      return;
    }
    depth.current += 1;
    setOver(true);
  }

  function onDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.dataTransfer.dropEffect = disabled ? "none" : "copy";
  }

  function onDragLeave() {
    if (disabled) return;
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setOver(false);
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    depth.current = 0;
    setOver(false);
    if (disabled) return;
    take(e.dataTransfer.files);
  }

  function onInputChange(e: ChangeEvent<HTMLInputElement>) {
    take(e.target.files);
    // Vacía el input para que elegir el mismo archivo otra vez (tras
    // corregirlo) vuelva a disparar change.
    e.target.value = "";
  }

  const zoneClass = ["ag-drop", over ? "ag-drop--over" : null, error ? "ag-drop--error" : null]
    .filter(Boolean)
    .join(" ");
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(" ");

  return (
    <div>
      <div
        className={zoneClass}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {icon === "image" ? <Icon.Photo s={24} /> : <UploadIcon />}
        <div className="ag-strong">{title}</div>
        {hint && (
          <div className="ag-muted" id={hintId}>
            {hint}
          </div>
        )}
        <label
          className={`btn btn--secondary btn--sm ag-drop__pick${disabled ? " ag-disabled" : ""}`}
          title={
            buttonTitle ??
            (multiple
              ? "Abre el selector de archivos para elegir los archivos"
              : "Abre el selector de archivos para elegir uno")
          }
        >
          {buttonLabel}
          <input
            type="file"
            className="sr-only"
            accept={accept}
            multiple={multiple}
            disabled={disabled}
            aria-describedby={describedBy || undefined}
            aria-invalid={error ? true : undefined}
            onChange={onInputChange}
          />
        </label>
      </div>
      {error && (
        <p id={errorId} className="ag-drop__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
